import React, {
  useCallback, useEffect, useMemo, useRef,
} from 'react';

import {
  bool, func, string,
} from 'prop-types';
import { useDispatch, useSelector, useStore } from 'react-redux';
import _debounce from 'lodash/debounce';

import { createProductVendor, listProductVendors } from '../../../../store/productVendors/actions';
import localization from '../../../../shared/strings';
import Dropdown from '../../../dropdown';
import {
  findVendorByNormalizedTitle,
  getCheckedVendorItems,
  shouldApplyVendorCreateResult,
} from './helpers';

const l10n = localization.PRODUCT_INFO.vendor;
const SEARCH_DEBOUNCE_MS = 300;

const VendorsDropdown = ({
  value,
  disabled,
  onSelect,
  onClear,
}) => {
  const dispatch = useDispatch();
  const store = useStore();
  const items = useSelector((state) => state.productVendors.items);
  const isLoading = useSelector((state) => state.productVendors.isLoading);
  const isPageLoading = useSelector((state) => state.productVendors.isPageLoading);
  const isLoaded = useSelector((state) => state.productVendors.isLoaded);
  const hasMore = useSelector((state) => state.productVendors.hasMore);
  const searchQuery = useSelector((state) => state.productVendors.searchQuery);
  /**
   * Guards against double-applying a freshly created title: the generic Dropdown component
   * auto-fires onCheckedHandler (as an array) once the newly created item lands in `items`,
   * independently of the manual apply below. Whichever path runs first "claims" the pending
   * creation so the other one becomes a no-op.
   */
  const isPendingCreateAppliedRef = useRef(false);
  /** last query string requested through `filterFunc`, used to tell a genuinely new query
   *  apart from Dropdown re-invoking `filterFunc` with the same text to fetch the next page. */
  const queryRef = useRef('');
  const pendingSearchRef = useRef(null);
  /** whether the store's cached `items` were already stale (unloaded, or narrowed by a leftover
   *  search from a previous mount) the moment this component first mounted. Captured once via
   *  the `useRef` initializer -- evaluating it again on later renders would also match a genuine,
   *  currently-relevant user search (same `isLoaded: true` / non-empty `searchQuery` shape),
   *  which would wipe the just-fetched results back to the unfiltered list. */
  const staleAtMountRef = useRef(!isLoaded || !!searchQuery);

  const checkedItems = useMemo(
    () => getCheckedVendorItems(items, value),
    [items, value],
  );

  const abortPendingSearch = useCallback(() => {
    pendingSearchRef.current?.abort?.();
    pendingSearchRef.current = null;
  }, []);

  useEffect(() => {
    if (staleAtMountRef.current && !isLoading) {
      abortPendingSearch();
      const request = dispatch(listProductVendors({ search: '', from: 0 }));
      pendingSearchRef.current = request;
      staleAtMountRef.current = false;
    }
  }, [dispatch, isLoading, abortPendingSearch]);

  const runSearch = useCallback(async (query, { isPageLoad = false } = {}) => {
    if (isPageLoad && (!hasMore || isPageLoading)) {
      return items;
    }

    abortPendingSearch();
    const from = isPageLoad ? items.length : 0;
    const request = dispatch(listProductVendors({ search: query, from }));
    pendingSearchRef.current = request;
    try {
      await request.unwrap();
    } catch (error) {
      /** aborted or failed request: fall through and resolve with whatever the store now holds */
    } finally {
      pendingSearchRef.current = null;
    }
    /**
     * store.getState() is unavoidable here: the reducer's merged/deduped `items` for this
     * dispatch only exists in the store the instant it's committed, not in this closure's
     * `items` (captured before the dispatch) nor available synchronously without waiting for
     * the next render.
     */
    return store.getState().productVendors.items;
  }, [store, dispatch, abortPendingSearch, items, hasMore, isPageLoading]);

  const debouncedSearchRef = useRef(
    _debounce(async (query, resolve) => {
      const results = await runSearch(query);
      if (queryRef.current !== query) return;
      resolve(results);
    }, SEARCH_DEBOUNCE_MS),
  );

  useEffect(() => () => {
    debouncedSearchRef.current.cancel();
    abortPendingSearch();
  }, [abortPendingSearch]);

  const filterFunc = useCallback((query) => new Promise((resolve) => {
    const trimmedQuery = query.trim();

    if (trimmedQuery === queryRef.current) {
      if (!trimmedQuery) {
        /** the generic Dropdown also re-invokes filterFunc('') on its own, any time its
         *  `checkedItems` prop changes (componentDidUpdate -> updateView -> setFilter('')) --
         *  which happens on every select/clear, independently of real pagination. Real
         *  empty-query pagination never reaches filterFunc at all (Dropdown's rowRenderer calls
         *  `loadNextPage` directly when there's no filter text), so an empty-query match here is
         *  always that spurious call, not a genuine "load next page" request. Resolve as a no-op
         *  instead of dispatching an unrequested fetch. */
        resolve(items);
        return;
      }
      /** Dropdown re-invokes filterFunc with the same non-empty text to page through search results */
      runSearch(trimmedQuery, { isPageLoad: true }).then(resolve);
      return;
    }

    queryRef.current = trimmedQuery;
    debouncedSearchRef.current(trimmedQuery, resolve);
  }), [runSearch, items]);

  const loadNextPage = useCallback(() => {
    if (!hasMore || isPageLoading) return;
    dispatch(listProductVendors({ search: '', from: items.length }));
  }, [dispatch, items.length, hasMore, isPageLoading]);

  const handleSelect = useCallback((selectedVendorOrList) => {
    /** Dropdown may call back with an array when it auto-selects a freshly created item */
    const isAutoSelectAfterCreate = Array.isArray(selectedVendorOrList);
    const selectedVendor = isAutoSelectAfterCreate
      ? selectedVendorOrList[0]
      : selectedVendorOrList;
    if (!selectedVendor) return;
    if (isAutoSelectAfterCreate) {
      if (isPendingCreateAppliedRef.current) return;
      isPendingCreateAppliedRef.current = true;
    }
    onSelect(selectedVendor);
  }, [onSelect]);

  const handleClear = useCallback(() => {
    onClear?.();
  }, [onClear]);

  const handleCreate = useCallback(async (query) => {
    const trimmedTitle = query.trim();
    if (!trimmedTitle) return;

    const existingVendor = findVendorByNormalizedTitle(items, trimmedTitle);
    if (existingVendor) {
      onSelect(existingVendor);
      return;
    }

    isPendingCreateAppliedRef.current = false;
    const result = await dispatch(createProductVendor({ title: trimmedTitle }));
    /** Dropdown's own auto-select already applied the freshly created item, nothing to do */
    if (isPendingCreateAppliedRef.current) return;
    /** genuine failure (network/5xx/permission): do not apply an unsaved title */
    if (!shouldApplyVendorCreateResult(result)) return;
    isPendingCreateAppliedRef.current = true;
    /** covers the "already exists" case: title is valid, but items.length didn't change
     *  so Dropdown never auto-fires */
    const vendorToApply = result.payload?.productVendor
      || findVendorByNormalizedTitle(items, trimmedTitle)
      || { title: trimmedTitle };
    onSelect(vendorToApply);
  }, [dispatch, items, onSelect]);

  const placeholder = isLoaded ? l10n.noVendorsFound : l10n.loadingVendors;

  return (
    <Dropdown
      items={items}
      checkedItems={checkedItems}
      type="keyword"
      showChips
      showDeletedOptions
      showAddButton={false}
      disabled={disabled}
      isOnlyCreate
      filterText={l10n.placeholder}
      placeholder={placeholder}
      createPlaceholderText={l10n.noVendorsFoundCreate}
      createHandler={handleCreate}
      isItemsLoaded={isLoaded}
      inProgress={isLoading || isPageLoading}
      hasNextPage={hasMore}
      loadNextPage={loadNextPage}
      filterFunc={filterFunc}
      additionalClass="vendorDropdown"
      closeOnSelect
      tooltipTextClose={l10n.clearTooltip}
      onCheckedHandler={handleSelect}
      onUncheckedHandler={handleClear}
    />
  );
};

VendorsDropdown.propTypes = {
  value: string,
  disabled: bool,
  onSelect: func.isRequired,
  onClear: func,
};

VendorsDropdown.defaultProps = {
  value: '',
  disabled: false,
  onClear: null,
};

export default VendorsDropdown;
